import { Button, Text } from '@/components';
import AnimatedLayout from '@/components/templates/AnimatedLayout';
import APP_CONFIG from '@/config/app';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const mainPadding = APP_CONFIG.STYLES.MAIN_PADDING;
const mainGap = APP_CONFIG.STYLES.MAIN_GAP;
const mainRoundedness = APP_CONFIG.STYLES.MAIN_ROUNDEDNESS;
const wholeStepDecrement = APP_CONFIG.STYLES.WHOLESTEP_DECREMENT;
const halfStepDecrement = APP_CONFIG.STYLES.HALFSTEP_DECREMENT;
const smallHeading = APP_CONFIG.STYLES.TEXT.SMALL_HEADING;
const smallHeadingColor = APP_CONFIG.STYLES.TEXT.SMALL_HEADING_COLOR;
const smallSubheading = APP_CONFIG.STYLES.TEXT.SMALL_SUBHEADING;
const smallSubheadingColor = APP_CONFIG.STYLES.TEXT.SMALL_SUBHEADING_COLOR;
const spacingConfig = {
  padding: `p${mainPadding}`,
  gap: `gap${mainGap}`,
  staticSuperHeaderPadding: 'p-0',
  headerPadding: 'p-0',
  bodyPadding: 'p-0',
  footerPadding: 'p-0',
  staticSuperHeaderGap: `gap${mainGap}`,
  headerGap: `gap${mainGap}`,
  headerGapBetweenMainHeaderAndMainHeaderCaption: `gap${mainGap}${halfStepDecrement}`,
  bodyGap: `gap${mainGap}`,
  footerGap: `gap${mainGap}`,
  footerMarginTop: `mt${mainGap}`,
  carouselIndicatorsContainerPadding: 'p-0',
  carouselIndicatorsSpacing: 'mx-1',
  carouselIndicatorsInnerContainerDisplay: 'flex',
  carouselIndicatorsInnerContainerAlignItems: 'center',
  carouselIndicatorsInnerContainerJustifyContent: 'center',
  chipGap: `gap${mainGap}${wholeStepDecrement}`,
};
const roundednessConfig = {
  roundedness: `rounded${mainRoundedness}`,
  carouselIndicatorsRoundedness: 'rounded-full',
};
const sizingConfig = {
  carouselIndicatorsHeight: '8px',
  carouselIndicatorsWidth: '8px',
};
const colorConfig = {
  backgroundColor: 'bg-primary/5',
  borderColor: 'border border-primary/5',
  carouselIndicatorsActiveColor: 'bg-accent',
  carouselIndicatorsInactiveColor: 'bg-accent/10',
  carouselIndicatorsActiveOpacity: 1,
  carouselIndicatorsInactiveOpacity: 0.65,
};
const animationConfig = {
  carouselIndicatorsTransitionDuration: 0.3,
  carouselIndicatorsActiveScaleValues: [1, 1.25, 1],
  carouselIndicatorsActiveOpacityValues: [0.7, 1, 0.7],
  carouselIndicatorsInactiveScaleValues: [0.9, 1, 0.9],
  carouselIndicatorsInactiveOpacityValues: [0.4, 0.65, 0.4],
  carouselIndicatorsAnimationDuration: 2.5,
  carouselIndicatorsAnimationRepeatDelay: 0.1,
  carouselIndicatorsAnimationEase: [0.4, 0.0, 0.2, 1],
  carouselIndicatorsHoverScale: 1.1,
  carouselIndicatorsHoverOpacity: 0.8,
};
const textConfig = {
  headerTitleSize: smallHeading,
  headerTitleColor: smallHeadingColor,
  subHeaderSize: smallSubheading,
  subHeaderColor: smallSubheadingColor,
};
export interface CarouselScreenProps {
  mainHeader?: ReactNode;
  mainHeaderCaption?: ReactNode;
  subHeader?: ReactNode;
  body: ReactNode;
  mainHeaderAlign?: 'left' | 'right' | 'center';
  subHeaderAlign?: 'left' | 'right' | 'center';
  chipLabel?: ReactNode;
  headerRightNode?: ReactNode;
}
interface CarouselSectionProps {
  screens: CarouselScreenProps[];
  showNavigationControlsInHeader?: boolean;
  autoNavigate?: boolean;
  autoNavigateInterval?: number;
  className?: string;
  staticSuperHeader?: string | ReactNode;
  isBackgroundTransparent?: boolean;
  isBorderTransparent?: boolean;
  isScrollable?: boolean;
  useChipNavigation?: boolean;
  leftAlignHeaderWhenNavigationControlsAreShown?: boolean;
}
const CarouselSection: FC<CarouselSectionProps> = ({
  screens,
  showNavigationControlsInHeader = false,
  autoNavigate = false,
  autoNavigateInterval = 3000,
  className = '',
  staticSuperHeader,
  isBackgroundTransparent = false,
  isBorderTransparent = false,
  isScrollable = false,
  useChipNavigation = false,
  leftAlignHeaderWhenNavigationControlsAreShown = false,
}) => {
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [animationTrigger, setAnimationTrigger] = useState(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef<number | null>(null);
  const touchEndXRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const SWIPE_THRESHOLD = 50;
  const currentScreen = useMemo(
    () => screens[currentScreenIndex],
    [screens, currentScreenIndex],
  );
  const bodyStyle = useMemo(
    () =>
      ({
        WebkitOverflowScrolling: isScrollable ? 'touch' : 'auto',
        maskImage: isScrollable
          ? 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 1) 5%, rgba(0, 0, 0, 1) 95%, rgba(0, 0, 0, 0.3))'
          : 'none',
        scrollbarWidth: isScrollable ? 'none' : 'auto',
        msOverflowStyle: isScrollable ? 'none' : 'auto',
      } as React.CSSProperties),
    [isScrollable],
  );
  const containerClassName = useMemo(
    () => `relative flex flex-col overflow-hidden scrollbar-hide 
    ${isScrollable ? 'h-full' : 'flex-basis-auto'} 
    ${isBackgroundTransparent ? '' : colorConfig.backgroundColor} 
    ${isBorderTransparent ? '' : colorConfig.borderColor} 
    ${roundednessConfig.roundedness} 
    ${spacingConfig.padding} 
    ${spacingConfig.gap} 
    ${className} 
    ${borderClassToVisualizeElementsAndSpacing}`,
    [
      isScrollable,
      isBackgroundTransparent,
      isBorderTransparent,
      colorConfig.backgroundColor,
      colorConfig.borderColor,
      roundednessConfig.roundedness,
      spacingConfig.padding,
      spacingConfig.gap,
      className,
      borderClassToVisualizeElementsAndSpacing,
    ],
  );
  const navigateToIndex = useCallback(
    (index: number) => {
      if (index > currentScreenIndex) {
        setDirection('right');
      } else if (index < currentScreenIndex) {
        setDirection('left');
      }
      setCurrentScreenIndex(index);
    },
    [currentScreenIndex],
  );
  const handleChipClick = useCallback(
    (index: number) => navigateToIndex(index),
    [navigateToIndex],
  );
  const handleNextScreen = useCallback(() => {
    setDirection('right');
    setCurrentScreenIndex((prevIndex) =>
      prevIndex === screens.length - 1 ? 0 : prevIndex + 1,
    );
  }, [screens.length]);
  const handlePreviousScreen = useCallback(() => {
    setDirection('left');
    setCurrentScreenIndex((prevIndex) =>
      prevIndex === 0 ? screens.length - 1 : prevIndex - 1,
    );
  }, [screens.length]);
  const handleIndicatorClick = useCallback(
    (index: number) => navigateToIndex(index),
    [navigateToIndex],
  );
  const renderChips = useCallback(() => {
    return (
      <div
        className={`flex flex-row items-center justify-center
          ${spacingConfig.chipGap} 
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        {screens.map((screen, index) => {
          const chipText = screen.chipLabel
            ? screen.chipLabel
            : typeof screen.mainHeader === 'string'
            ? screen.mainHeader
            : 'Screen ' + (index + 1);
          return (
            <Button
              key={index}
              onClick={() => handleChipClick(index)}
              intent={'chip'}
              active={index === currentScreenIndex}
            >
              {chipText}
            </Button>
          );
        })}
      </div>
    );
  }, [
    screens,
    currentScreenIndex,
    handleChipClick,
    spacingConfig.chipGap,
    borderClassToVisualizeElementsAndSpacing,
  ]);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartXRef.current = e.targetTouches[0].clientX;
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndXRef.current = e.targetTouches[0].clientX;
  }, []);
  const handleTouchEnd = useCallback(() => {
    if (touchStartXRef.current !== null && touchEndXRef.current !== null) {
      const touchDiff = touchStartXRef.current - touchEndXRef.current;
      if (touchDiff > SWIPE_THRESHOLD) {
        handleNextScreen();
      } else if (touchDiff < -SWIPE_THRESHOLD) {
        handlePreviousScreen();
      }
    }
    touchStartXRef.current = null;
    touchEndXRef.current = null;
    if (autoNavigate) {
      intervalRef.current = window.setInterval(
        handleNextScreen,
        autoNavigateInterval,
      );
    }
  }, [
    autoNavigate,
    autoNavigateInterval,
    handleNextScreen,
    handlePreviousScreen,
  ]);
  const indicatorContainerStyle = useMemo(
    () =>
      ({
        display: spacingConfig.carouselIndicatorsInnerContainerDisplay,
        alignItems: spacingConfig.carouselIndicatorsInnerContainerAlignItems,
        justifyContent:
          spacingConfig.carouselIndicatorsInnerContainerJustifyContent,
      } as React.CSSProperties),
    [
      spacingConfig.carouselIndicatorsInnerContainerDisplay,
      spacingConfig.carouselIndicatorsInnerContainerAlignItems,
      spacingConfig.carouselIndicatorsInnerContainerJustifyContent,
    ],
  );
  const setupAutoNavigation = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoNavigate) {
      intervalRef.current = window.setInterval(
        handleNextScreen,
        autoNavigateInterval,
      );
    }
  }, [autoNavigate, autoNavigateInterval, handleNextScreen]);
  useEffect(() => {
    if (autoNavigate) {
      setAnimationTrigger((prev) => prev + 1);
      setupAutoNavigation();
      return () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
      };
    }
  }, [autoNavigate, setupAutoNavigation]);
  useEffect(() => {
    setupAutoNavigation();
  }, [currentScreenIndex, setupAutoNavigation]);
  return (
    <div className={containerClassName}>
      {staticSuperHeader && (
        <div
          className={`z-20 relative flex flex-col items-start justify-start 
            ${spacingConfig.staticSuperHeaderPadding} 
            ${spacingConfig.staticSuperHeaderGap} 
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          {typeof staticSuperHeader === 'string' ? (
            <Text
              value={staticSuperHeader}
              size="h4"
              className={borderClassToVisualizeElementsAndSpacing}
            />
          ) : (
            staticSuperHeader
          )}
        </div>
      )}
      {(showNavigationControlsInHeader ||
        currentScreen?.mainHeader ||
        currentScreen?.mainHeaderCaption ||
        currentScreen?.subHeader) && (
        <div
          className={`relative z-20 
          ${spacingConfig.headerPadding} 
          ${className}
          ${borderClassToVisualizeElementsAndSpacing}`}
        >
          <div
            className={`flex items-center justify-between w-full ${borderClassToVisualizeElementsAndSpacing}`}
          >
            <div
              className={`flex items-center 
              ${spacingConfig.headerGap} 
              ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {showNavigationControlsInHeader && screens.length > 1 && (
                <Button
                  onClick={handlePreviousScreen}
                  className={borderClassToVisualizeElementsAndSpacing}
                  icon="chevronLeft"
                  intent={`ghost`}
                />
              )}
              {leftAlignHeaderWhenNavigationControlsAreShown &&
                currentScreen?.mainHeader && (
                  <div
                    className={`flex items-center ${borderClassToVisualizeElementsAndSpacing}`}
                  >
                    {currentScreen.mainHeader}
                  </div>
                )}
            </div>
            {!leftAlignHeaderWhenNavigationControlsAreShown &&
              showNavigationControlsInHeader && (
                <div
                  className={`absolute inset-0 flex items-center justify-center pointer-events-none ${borderClassToVisualizeElementsAndSpacing}`}
                >
                  <div
                    className={`pointer-events-auto ${borderClassToVisualizeElementsAndSpacing}`}
                  >
                    {currentScreen?.mainHeader}
                  </div>
                </div>
              )}
            <div
              className={`flex items-center 
              ${spacingConfig.headerGap} 
              ${borderClassToVisualizeElementsAndSpacing}`}
            >
              {currentScreen?.subHeader}
              {currentScreen?.headerRightNode}
              {showNavigationControlsInHeader && screens.length > 1 && (
                <Button
                  onClick={handleNextScreen}
                  className={borderClassToVisualizeElementsAndSpacing}
                  icon="chevronRight"
                  intent={`ghost`}
                />
              )}
            </div>
          </div>
        </div>
      )}
      <div
        ref={bodyRef}
        className={`z-10 relative 
          ${isScrollable ? 'flex-1 overflow-y-auto' : 'flex-basis-auto'} 
          ${spacingConfig.bodyPadding} 
          ${spacingConfig.bodyGap} 
          ${borderClassToVisualizeElementsAndSpacing}`}
        style={bodyStyle}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <AnimatedLayout uniqueKey={currentScreenIndex} slideFrom={direction}>
            {currentScreen?.body}
          </AnimatedLayout>
        </AnimatePresence>
      </div>
      {screens.length > 1 && (
        <div
          className={`relative z-20 
            ${spacingConfig.footerPadding} 
            ${spacingConfig.footerGap} 
            ${spacingConfig.footerMarginTop}
            ${borderClassToVisualizeElementsAndSpacing}`}
        >
          <div
            className={`flex items-center justify-center 
              ${spacingConfig.carouselIndicatorsContainerPadding} 
              ${borderClassToVisualizeElementsAndSpacing}`}
          >
            {useChipNavigation
              ? renderChips()
              : screens.map((_, index) => {
                  const isActive = index === currentScreenIndex;
                  return (
                    <div
                      key={index}
                      className={`cursor-pointer ${spacingConfig.carouselIndicatorsSpacing} ${borderClassToVisualizeElementsAndSpacing}`}
                      onClick={() => handleIndicatorClick(index)}
                      style={indicatorContainerStyle}
                    >
                      <motion.div
                        className={`${
                          roundednessConfig.carouselIndicatorsRoundedness
                        } ${
                          isActive
                            ? colorConfig.carouselIndicatorsActiveColor
                            : colorConfig.carouselIndicatorsInactiveColor
                        } ${borderClassToVisualizeElementsAndSpacing}`}
                        key={`indicator-${index}-${
                          autoNavigate ? `auto-${animationTrigger}` : 'manual'
                        }`}
                        initial={{
                          width: sizingConfig.carouselIndicatorsWidth,
                          height: sizingConfig.carouselIndicatorsHeight,
                          opacity: isActive
                            ? colorConfig.carouselIndicatorsActiveOpacity
                            : colorConfig.carouselIndicatorsInactiveOpacity,
                          scale: 1,
                        }}
                        whileHover={{
                          scale: animationConfig.carouselIndicatorsHoverScale,
                          opacity:
                            animationConfig.carouselIndicatorsHoverOpacity,
                          transition: {
                            duration:
                              animationConfig.carouselIndicatorsTransitionDuration,
                          },
                        }}
                        animate={{
                          width: sizingConfig.carouselIndicatorsWidth,
                          height: sizingConfig.carouselIndicatorsHeight,
                          opacity: isActive
                            ? autoNavigate
                              ? animationConfig.carouselIndicatorsActiveOpacityValues
                              : colorConfig.carouselIndicatorsActiveOpacity
                            : autoNavigate
                            ? animationConfig.carouselIndicatorsInactiveOpacityValues
                            : colorConfig.carouselIndicatorsInactiveOpacity,
                          scale: isActive
                            ? autoNavigate
                              ? animationConfig.carouselIndicatorsActiveScaleValues
                              : 1
                            : autoNavigate
                            ? animationConfig.carouselIndicatorsInactiveScaleValues
                            : 1,
                        }}
                        transition={{
                          opacity: {
                            duration:
                              animationConfig.carouselIndicatorsAnimationDuration,
                            ease: animationConfig.carouselIndicatorsAnimationEase,
                            repeat: autoNavigate ? Infinity : 0,
                            repeatDelay:
                              animationConfig.carouselIndicatorsAnimationRepeatDelay,
                          },
                          scale: {
                            duration:
                              animationConfig.carouselIndicatorsAnimationDuration,
                            ease: animationConfig.carouselIndicatorsAnimationEase,
                            repeat: autoNavigate ? Infinity : 0,
                            repeatDelay:
                              animationConfig.carouselIndicatorsAnimationRepeatDelay,
                          },
                        }}
                      />
                    </div>
                  );
                })}
          </div>
        </div>
      )}
      {isScrollable && (
        <style jsx>{`
          .flex-1::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      )}
    </div>
  );
};
export default CarouselSection;
